/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { MatchPosition, Tags } from 'store/types';
import isEmpty from 'lodash-es/isEmpty';
import reduce from 'lodash-es/reduce';
import keys from 'lodash-es/keys';
import omit from 'lodash-es/omit';
import {
  fetchMatched,
  selectStartTags,
  selectTagsPage,
  selectTopTags,
} from 'store/tagsSlice';
import { isSearchEnoughtLarge } from 'utils/utils';

// Реализует непосредственно поиск и хранит поисковые объекты
// Можно понимать этот компонент, как модель всего поиска
export const useSearch = ({
  isURLSync,
  matchPosition,
}: {
  isURLSync?: boolean;
  matchPosition: MatchPosition;
}) => {
  const dispatch = useAppDispatch();
  const storeTop = useAppSelector(selectTopTags);
  const startTags = useAppSelector(selectStartTags);
  const allTags = useAppSelector(selectTagsPage);

  const [selected, setSelected] = useState<Tags>({});
  const [available, setAvailable] = useState<Tags>({});
  const [top, setTop] = useState<Tags>(storeTop);

  const [search, setSearch] = useState('');
  const [matchResponse, setMatchResponse] = useState<Tags>({});
  const [match, setMatch] = useState({});
  const [availableHash, setAvailableHash] = useState(
    reduce(
      keys(available),
      (acc, key) => {
        return acc + key;
      },
      ''
    )
  );

  // Если есть опция синхронизации поиска со строкой поиска, нужно обновить
  useEffect(() => {
    if (!isURLSync) {
      return;
    }

    setSelected(startTags);
  }, [startTags]);

  // При изменении топ тегов или выбранных тегов, актуализируем их локальные контейнеры
  useEffect(() => {
    setTop(omit(storeTop, keys(selected)));
  }, [storeTop, selected]);

  // Постообработка совпавших тегов из ответа
  const processTags = () => {
    let newHash = '';
    const matched = reduce(
      matchResponse,
      (acc: Tags, tag) => {
        if (selected[tag.name]) {
          return acc;
        }

        newHash += tag.name;
        acc[tag.name] = tag;

        return acc;
      },
      {}
    );

    setAvailableHash(newHash);

    if (isEmpty(matched)) {
      setMatch({});
    } else {
      setMatch(matched);
    }
  };

  useEffect(() => {
    const f = async () => {
      if (isSearchEnoughtLarge(search)) {
        const result = await dispatch(
          fetchMatched({
            search,
            position: matchPosition,
          })
        );

        if (result.meta.requestStatus === 'fulfilled' && result.payload) {
          setMatchResponse(result.payload as Tags);
        }
      } else {
        setMatch({});
      }
    };

    f();
  }, [search, allTags]);

  useEffect(() => {
    if (isSearchEnoughtLarge(search)) {
      processTags();
    } else {
      setMatch({});
    }
  }, [selected, matchResponse]);

  return {
    // Совпашвие с поиском теги либо топ тегов, если строка поиска пуста или совпадений не найдено
    match,
    // Текущая строка поиска
    search,
    setSearch,
    selected,
    setSelected,
    available,
    setAvailable,
    availableHash,
    top,
  };
};
